import type { ArdoqId } from '@ardoq/api-types';
import type { PermissionContext } from '@ardoq/access-control';
import type { FormattingAndLabelRules } from '@ardoq/data-model';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import {
  toSpiderPlot,
  type RadialPlot,
  type SpiderPlotArgs,
  type SpiderViewSettings,
} from '@ardoq/xyz-charts';
import {
  numericFieldsInScope,
  readFieldValueFromScopeData,
} from 'streams/fields/numericFieldsInScope';
import type { ChartEmptyReason, NumericField } from 'tabview/chartShared/types';
import { toComponentLabels } from 'tabview/chartShared/componentLabels';
import { toFieldOptions } from 'tabview/chartShared/fieldLabel';

export const MINIMUM_SPOKES = 3;

export type SpiderSelection = Pick<SpiderViewSettings, 'selectedFieldNames'>;

export type SpiderSourceDataArgs = {
  viewSettings: SpiderSelection;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
};

export type SpiderSourceData = {
  viewInstanceId: string;
  plot: RadialPlot;
  /** The components the chart draws, which is what a scenario from the view is built from. */
  points: { componentId: ArdoqId }[];
  excludedCount: number;
  numericFields: NumericField[];
  isViewEmpty: boolean;
  emptyReason: ChartEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export type SpiderComponent = {
  id: ArdoqId;
  label: string;
  /** One per selected field, in field order. */
  values: (number | null)[];
};

export type SpiderPlotInput = {
  plotArgs: SpiderPlotArgs;
  plotted: SpiderComponent[];
  excludedCount: number;
  emptyReason: ChartEmptyReason;
};

const hasAnyValue = ({ values }: SpiderComponent): boolean =>
  values.some(value => value !== null);

/** Selected fields in picker order, dropping any this viewpoint does not offer. */
export const toSelectedFields = (
  numericFields: NumericField[],
  selectedFieldNames: string[]
): NumericField[] => {
  const selected = new Set(selectedFieldNames);
  return numericFields.filter(({ name }) => selected.has(name));
};

/** One shape per field, with a spoke per component. */
const toPlotArgs = (
  fields: NumericField[],
  components: SpiderComponent[]
): SpiderPlotArgs => ({
  spokes: components.map(({ id, label }) => ({ key: id, label })),
  series: fields.map(({ name, label }, fieldIndex) => ({
    key: name,
    label,
    values: components.map(({ values }) => values[fieldIndex]),
  })),
  radialTitle: '',
});

const EMPTY_PLOT_ARGS: SpiderPlotArgs = {
  spokes: [],
  series: [],
  radialTitle: '',
};

export const toSpiderPlotInput = (
  fields: NumericField[],
  components: SpiderComponent[]
): SpiderPlotInput => {
  const plotted = components.filter(hasAnyValue);
  const excludedCount = components.length - plotted.length;

  if (plotted.length < MINIMUM_SPOKES) {
    return {
      plotArgs: EMPTY_PLOT_ARGS,
      plotted,
      excludedCount,
      emptyReason: 'too few positions',
    };
  }

  return {
    plotArgs: toPlotArgs(fields, plotted),
    plotted,
    excludedCount,
    emptyReason: null,
  };
};

type EmptySourceDataArgs = {
  viewInstanceId: string;
  numericFields: NumericField[];
  emptyReason: ChartEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const toEmptySourceData = ({
  viewInstanceId,
  numericFields,
  emptyReason,
  locale,
  i18nLocale,
}: EmptySourceDataArgs): SpiderSourceData => ({
  viewInstanceId,
  plot: toSpiderPlot(EMPTY_PLOT_ARGS),
  points: [],
  excludedCount: 0,
  numericFields,
  isViewEmpty: true,
  emptyReason,
  locale,
  i18nLocale,
});

export const buildSpiderSourceData = (
  viewInstanceId: string,
  {
    viewSettings: { selectedFieldNames },
    loadedGraph,
    permissionContext,
    locale,
    i18nLocale,
    formattingAndLabelRules,
  }: SpiderSourceDataArgs
): SpiderSourceData => {
  const { scopeData, scopeComponentIds } = loadedGraph;
  const numericFields = toFieldOptions(
    numericFieldsInScope(scopeData, permissionContext)
  );
  const empty = (emptyReason: ChartEmptyReason) =>
    toEmptySourceData({
      viewInstanceId,
      numericFields,
      emptyReason,
      locale,
      i18nLocale,
    });

  if (scopeComponentIds.length === 0) return empty('no components');
  if (numericFields.length === 0) return empty('no numeric fields');

  const fields = toSelectedFields(numericFields, selectedFieldNames);
  if (fields.length === 0) return empty('no fields selected');

  const labels = toComponentLabels({
    enhancedScopeData: scopeData,
    componentIds: scopeComponentIds,
    formattingAndLabelRules,
    i18nLocale,
  });
  const readValue = readFieldValueFromScopeData(scopeData);
  const components = scopeComponentIds.map(id => ({
    id,
    label: labels.get(id) ?? '',
    values: fields.map(({ name }) => readValue(id, name)),
  }));

  const { plotArgs, plotted, excludedCount, emptyReason } = toSpiderPlotInput(
    fields,
    components
  );

  return {
    viewInstanceId,
    plot: toSpiderPlot(plotArgs),
    points: plotted.map(({ id }) => ({ componentId: id })),
    excludedCount,
    numericFields,
    isViewEmpty: emptyReason !== null,
    emptyReason,
    locale,
    i18nLocale,
  };
};
